import { type LeadTemplate, type VariableContext } from "./lead-types.ts";

export const BUILT_IN_LEAD_TEMPLATES: LeadTemplate[] = [
  {
    id: "lead_expense_management",
    name: "Expense Management & Corporate Cards",
    category: "expense_management",
    description: "Tailored for expense tracking, receipt scanning, and corporate spend platforms.",
    subject: "Currency conversion and FX rates for {{company}}",
    htmlBody: `<p>Hi {{name}},</p>

<p>I came across {{company}} and really liked how you are simplifying expense tracking for teams.</p>

<p>I noticed that handling multi-currency receipts and corporate card expenses often comes with headaches: volatile FX spreads, delayed bank rate refreshes, and manual reconciliation discrepancies.</p>

<p>We built <strong>CurrencyCore</strong> to solve this specifically for financial tools and spend management platforms. We provide:</p>

<ul>
  <li><strong>Real-time mid-market rates:</strong> Verified against official central bank and ECB feeds with zero hidden spreads.</li>
  <li><strong>Precise historical lookup:</strong> Query exchange rates down to the exact second a transaction took place for audit-proof reconciliation.</li>
  <li><strong>Ultra-low latency:</strong> Sub-millisecond response times cached across 300+ edge locations worldwide.</li>
</ul>

<p>Would you be open to a quick 5-minute chat or testing our free developer tier to see if it could help {{company}} save time on currency conversion?</p>

<p>Best regards,<br>
{{sender_name}}<br>
CurrencyCore Team (<a href="https://currency-core.com">currency-core.com</a>)</p>`,
    textBody: `Hi {{name}},

I came across {{company}} and really liked how you are simplifying expense tracking for teams.

I noticed that handling multi-currency receipts and corporate card expenses often comes with headaches: volatile FX spreads, delayed bank rate refreshes, and manual reconciliation discrepancies.

We built CurrencyCore to solve this specifically for financial tools and spend management platforms. We provide:
- Real-time mid-market rates: Verified against official central bank and ECB feeds with zero hidden spreads.
- Precise historical lookup: Query exchange rates down to the exact second a transaction took place for audit-proof reconciliation.
- Ultra-low latency: Sub-millisecond response times cached across 300+ edge locations worldwide.

Would you be open to a quick 5-minute chat or testing our free developer tier to see if it could help {{company}} save time on currency conversion?

Best regards,
{{sender_name}}
CurrencyCore Team (currency-core.com)`,
    isBuiltIn: true,
  },
  {
    id: "lead_saas_pricing",
    name: "SaaS Localized Pricing & PPP",
    category: "saas_pricing",
    description: "For SaaS apps looking to increase international conversions via localized pricing.",
    subject: "Boosting international conversions for {{company}} with localized pricing",
    htmlBody: `<p>Hi {{name}},</p>

<p>I was exploring {{company}} and loved the product you have built at {{website}}.</p>

<p>One trend we have observed across growing SaaS companies is that displaying prices strictly in USD or EUR can lead to high drop-offs from international visitors. Implementing automatic localized currency displays and purchasing power parity (PPP) frequently boosts international checkout conversions by 20% to 35%.</p>

<p>We built <strong>CurrencyCore</strong> to make localized pricing seamless for developers:</p>

<ul>
  <li><strong>Instant IP-to-Currency mapping:</strong> Automatically show visitors prices in their home currency.</li>
  <li><strong>Reliable rate caching:</strong> Edge-cached exchange rates that load in less than 5ms so your pricing page never lags.</li>
  <li><strong>Drop-in integration:</strong> Simple JSON API that plugs into your existing Stripe or custom billing setup.</li>
</ul>

<p>Would you like to try out a free developer token to test localized pricing on {{company}}?</p>

<p>Best regards,<br>
{{sender_name}}<br>
CurrencyCore Team (<a href="https://currency-core.com">currency-core.com</a>)</p>`,
    textBody: `Hi {{name}},

I was exploring {{company}} and loved the product you have built at {{website}}.

One trend we have observed across growing SaaS companies is that displaying prices strictly in USD or EUR can lead to high drop-offs from international visitors. Implementing automatic localized currency displays and purchasing power parity (PPP) frequently boosts international checkout conversions by 20% to 35%.

We built CurrencyCore to make localized pricing seamless for developers:
- Instant IP-to-Currency mapping: Automatically show visitors prices in their home currency.
- Reliable rate caching: Edge-cached exchange rates that load in less than 5ms so your pricing page never lags.
- Drop-in integration: Simple JSON API that plugs into your existing Stripe or custom billing setup.

Would you like to try out a free developer token to test localized pricing on {{company}}?

Best regards,
{{sender_name}}
CurrencyCore Team (currency-core.com)`,
    isBuiltIn: true,
  },
  {
    id: "lead_invoicing_payroll",
    name: "Cross-Border Invoicing & Global Payroll",
    category: "invoicing_payroll",
    description: "For platforms handling contractor billing, international invoices, and remittances.",
    subject: "Transparent exchange rates and multi-currency invoicing for {{company}}",
    htmlBody: `<p>Hi {{name}},</p>

<p>I noticed {{company}} powers cross-border billing and contractor workflows.</p>

<p>One of the most common friction points in cross-border invoicing is exchange rate dispute when invoices are created in one currency and settled in another. Relying on delayed bank updates or volatile retail rates creates uncertainty for both clients and contractors.</p>

<p><strong>CurrencyCore</strong> provides a transparent, developer-friendly rate infrastructure:</p>

<ul>
  <li><strong>Tamper-proof audit trails:</strong> Reference ECB and central bank rates locked to invoice creation timestamps.</li>
  <li><strong>Zero hidden margins:</strong> Clean mid-market rates that keep billing fair and transparent.</li>
  <li><strong>High availability:</strong> 99.99% uptime with redundant failover across multiple tier-1 FX providers.</li>
</ul>

<p>I would love to send over sandbox credentials if you would like to test our historical conversion endpoint. Let me know if you would like a quick demo!</p>

<p>Best regards,<br>
{{sender_name}}<br>
CurrencyCore Team (<a href="https://currency-core.com">currency-core.com</a>)</p>`,
    textBody: `Hi {{name}},

I noticed {{company}} powers cross-border billing and contractor workflows.

One of the most common friction points in cross-border invoicing is exchange rate dispute when invoices are created in one currency and settled in another. Relying on delayed bank updates or volatile retail rates creates uncertainty for both clients and contractors.

CurrencyCore provides a transparent, developer-friendly rate infrastructure:
- Tamper-proof audit trails: Reference ECB and central bank rates locked to invoice creation timestamps.
- Zero hidden margins: Clean mid-market rates that keep billing fair and transparent.
- High availability: 99.99% uptime with redundant failover across multiple tier-1 FX providers.

I would love to send over sandbox credentials if you would like to test our historical conversion endpoint. Let me know if you would like a quick demo!

Best regards,
{{sender_name}}
CurrencyCore Team (currency-core.com)`,
    isBuiltIn: true,
  },
  {
    id: "lead_ecommerce",
    name: "E-Commerce & Multi-Currency Checkouts",
    category: "ecommerce",
    description: "For e-commerce stores and marketplaces requiring fast checkout currency conversions.",
    subject: "Instant multi-currency checkout rates for {{company}}",
    htmlBody: `<p>Hi {{name}},</p>

<p>I saw {{company}} and the international shopping experience you provide at {{website}}.</p>

<p>Cart abandonment often spikes when international shoppers cannot see their final checkout total in their local currency, or when currency conversion scripts add perceptible latency to page load speeds.</p>

<p>We built <strong>CurrencyCore</strong> to deliver the fastest currency conversion engine available:</p>

<ul>
  <li><strong>Sub-10ms checkout conversions:</strong> Deployed globally on Cloudflare edge workers right next to your shoppers.</li>
  <li><strong>Live & historical rates:</strong> Real-time FX updates for over 160 currencies and precious metals.</li>
  <li><strong>Reliable failover:</strong> Redundant institutional sources with guaranteed high availability.</li>
</ul>

<p>Would you like a free API key to benchmark our speed and reliability against your current currency provider?</p>

<p>Best regards,<br>
{{sender_name}}<br>
CurrencyCore Team (<a href="https://currency-core.com">currency-core.com</a>)</p>`,
    textBody: `Hi {{name}},

I saw {{company}} and the international shopping experience you provide at {{website}}.

Cart abandonment often spikes when international shoppers cannot see their final checkout total in their local currency, or when currency conversion scripts add perceptible latency to page load speeds.

We built CurrencyCore to deliver the fastest currency conversion engine available:
- Sub-10ms checkout conversions: Deployed globally on Cloudflare edge workers right next to your shoppers.
- Live & historical rates: Real-time FX updates for over 160 currencies and precious metals.
- Reliable failover: Redundant institutional sources with guaranteed high availability.

Would you like a free API key to benchmark our speed and reliability against your current currency provider?

Best regards,
{{sender_name}}
CurrencyCore Team (currency-core.com)`,
    isBuiltIn: true,
  },
  {
    id: "lead_followup_bump",
    name: "Quick Check-in / Gentle Bump",
    category: "follow_up",
    description: "A short, polite 3-sentence follow-up to bump your initial note without being pushy.",
    subject: "Quick check-in regarding {{company}} and currency conversion",
    htmlBody: `<p>Hi {{name}},</p>

<p>I wanted to follow up briefly on my earlier note regarding currency conversion and exchange rates for {{company}}.</p>

<p>I know you are busy, so no worries if the timing is not right. If currency rates or international checkout is on your roadmap for this quarter, I would be glad to share a free developer sandbox key whenever you are ready.</p>

<p>Either way, keep up the great work with {{company}}!</p>

<p>Best regards,<br>
{{sender_name}}<br>
CurrencyCore Team (<a href="https://currency-core.com">currency-core.com</a>)</p>`,
    textBody: `Hi {{name}},

I wanted to follow up briefly on my earlier note regarding currency conversion and exchange rates for {{company}}.

I know you are busy, so no worries if the timing is not right. If currency rates or international checkout is on your roadmap for this quarter, I would be glad to share a free developer sandbox key whenever you are ready.

Either way, keep up the great work with {{company}}!

Best regards,
{{sender_name}}
CurrencyCore Team (currency-core.com)`,
    isBuiltIn: true,
    isFollowUp: true,
  },
  {
    id: "lead_followup_sandbox",
    name: "Value-Add Follow-up (Free Developer Tier)",
    category: "follow_up",
    description: "Follow-up highlighting zero-commitment developer sandbox and quick-start docs.",
    subject: "Developer sandbox key for {{company}}",
    htmlBody: `<p>Hi {{name}},</p>

<p>Following up quickly on {{company}}.</p>

<p>We just released our updated developer documentation and an expanded free tier (up to 1,000 requests/month with real-time ECB rates and zero credit card required).</p>

<p>If you or your engineering team ever want to test currency conversion or historical rates, you can explore the live sandbox here: <a href="https://currency-core.com">currency-core.com</a></p>

<p>Always happy to answer any technical questions or support your team.</p>

<p>Best regards,<br>
{{sender_name}}<br>
CurrencyCore Team (<a href="https://currency-core.com">currency-core.com</a>)</p>`,
    textBody: `Hi {{name}},

Following up quickly on {{company}}.

We just released our updated developer documentation and an expanded free tier (up to 1,000 requests/month with real-time ECB rates and zero credit card required).

If you or your engineering team ever want to test currency conversion or historical rates, you can explore the live sandbox here: currency-core.com

Always happy to answer any technical questions or support your team.

Best regards,
{{sender_name}}
CurrencyCore Team (currency-core.com)`,
    isBuiltIn: true,
    isFollowUp: true,
  },
];

/**
 * Replace all {{key}} occurrences in template text with variable values.
 */
export function interpolateLeadTemplate(
  text: string,
  variables: Record<string, string | undefined>,
): string {
  return text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_match, key: string) => {
    const val = variables[key];
    if (val !== undefined && val !== null && val.trim() !== "") {
      return val.trim();
    }
    // Return placeholder intact if value not provided
    return `{{${key}}}`;
  });
}

/**
 * Scan text for any remaining unfilled {{variable}} tokens.
 */
export function getUnfilledVariables(text: string): string[] {
  const matches = text.match(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g) ?? [];
  const unique = new Set<string>();
  for (const m of matches) {
    const clean = m.replace(/[\{\}\s]/g, "");
    unique.add(clean);
  }
  return Array.from(unique);
}

/**
 * Available variable tokens for insertion chips.
 */
export const LEAD_VARIABLE_TOKENS: { token: string; label: string; description: string }[] = [
  { token: "{{company}}", label: "Company", description: "Target company name" },
  { token: "{{name}}", label: "Contact Name", description: "Recipient's first name" },
  { token: "{{website}}", label: "Website", description: "Target website URL" },
  { token: "{{use_case}}", label: "Use Case", description: "Specific feature or use case" },
  { token: "{{pain_point}}", label: "Pain Point", description: "Specific problem or challenge" },
  { token: "{{sender_name}}", label: "Sender Name", description: "Your name or team" },
  { token: "{{email}}", label: "Email", description: "Lead's email address" },
];
